import 'reflect-metadata'
import type { Endpoint } from '@honorest/contract'

/**
 * Metadata key for storing contract endpoints
 */
const CONTRACT_METADATA_KEY = Symbol('honorest:contract:endpoint')

/**
 * Registry for managing contract metadata
 * Stores contract endpoint definitions attached to controller methods
 */
export class ContractMetadataRegistry {
	/**
	 * Stores an endpoint contract for a specific controller method
	 *
	 * @param target - The controller class
	 * @param propertyKey - The method name
	 * @param endpoint - The contract endpoint definition
	 */
	static setContract(target: object, propertyKey: string | symbol, endpoint: Endpoint): void {
		Reflect.defineMetadata(CONTRACT_METADATA_KEY, endpoint, target, propertyKey)
	}

	/**
	 * Retrieves the endpoint contract for a specific controller method
	 *
	 * @param target - The controller class
	 * @param propertyKey - The method name
	 * @returns The contract endpoint definition, or undefined if not found
	 */
	static getContract(target: object, propertyKey: string | symbol): Endpoint | undefined {
		return Reflect.getMetadata(CONTRACT_METADATA_KEY, target, propertyKey)
	}

	/**
	 * Checks if a controller method has a contract attached
	 *
	 * @param target - The controller class
	 * @param propertyKey - The method name
	 * @returns True if the method has a contract, false otherwise
	 */
	static hasContract(target: object, propertyKey: string | symbol): boolean {
		return Reflect.hasMetadata(CONTRACT_METADATA_KEY, target, propertyKey)
	}

	/**
	 * Removes the contract metadata for a specific controller method
	 *
	 * @param target - The controller class
	 * @param propertyKey - The method name
	 */
	static deleteContract(target: object, propertyKey: string | symbol): void {
		Reflect.deleteMetadata(CONTRACT_METADATA_KEY, target, propertyKey)
	}
}
